// client/src/AuthProvider.jsx
import React, {
  createContext,
  useContext,
  useState,
} from 'react';

import SignInModal from './SignInModal';
import api from './api';
import { shouldAutoCloseAuthModal } from './authFlow';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Read synchronously so the first render already knows who is signed in;
// otherwise protected routes like /new redirect away on refresh.
function readSavedSession() {
  try {
    const raw = localStorage.getItem('auth_user');
    const savedToken = localStorage.getItem('auth_token');

    if (raw && savedToken) {
      return { user: JSON.parse(raw), token: savedToken };
    }
  } catch (err) {
    console.warn('Could not restore authentication:', err);
  }

  return { user: null, token: null };
}

export default function AuthProvider({ children }) {
  const [user, setUser] = useState(() => readSavedSession().user);
  const [token, setToken] = useState(() => readSavedSession().token);
  const [open, setOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login');

  const signIn = () => {
    setAuthMode('login');
    setOpen(true);
  };

  const signUp = () => {
    setAuthMode('register');
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
  };

  const onSignedIn = (signedInUser, options = {}) => {
    setUser(signedInUser);
    setToken(signedInUser?.token || 'cookie');

    localStorage.setItem(
      'auth_user',
      JSON.stringify(signedInUser)
    );

    localStorage.setItem(
      'auth_token',
      signedInUser?.token || 'cookie'
    );

    if (options.keepOpen || !shouldAutoCloseAuthModal(options.mode || 'login')) {
      return;
    }

    close();
  };

  const signOut = async () => {
    try {
      await api.logout?.();
    } catch (err) {
      console.warn('Logout request failed:', err);
    }

    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_token');

    setUser(null);
    setToken(null);
  };

  // Merge changed details (e.g. a new profile picture) into the signed-in user.
  const updateUser = (changes) => {
    setUser((current) => {
      if (!current) return current;
      const next = { ...current, ...changes };
      try {
        localStorage.setItem('auth_user', JSON.stringify(next));
      } catch (error) {
        // Not saved on this device; still updated on screen.
      }
      return next;
    });
  };

  const value = {
    user,
    token,
    signIn,
    signUp,
    signOut,
    onSignedIn,
    setUser,
    updateUser,
  };

  return (
    <>
      <AuthContext.Provider value={value}>
        {children}
      </AuthContext.Provider>

      <SignInModal
        open={open}
        initialMode={authMode}
        onClose={close}
        onSignedIn={onSignedIn}
      />
    </>
  );
}